import { useEffect, useRef, useState } from 'react';
import { getUserSettings, setUserSettings, getStravaConnection, disconnectStrava, getErrorMessage } from '../api/api';
import './UserSettings.css';

const DISTANCE_UNITS = [
  { value: 'KILOMETRES', label: 'Kilometres (km)' },
  { value: 'MILES', label: 'Miles (mi)' },
];

const ELEVATION_UNITS = [
  { value: 'METRES', label: 'Metres (m)' },
  { value: 'FEET', label: 'Feet (ft)' },
];

// Each connected activity source gets its own tab in the Connections panel.
// Strava is the only one wired up today, but the tab list (and the
// per-source connect/disconnect state below) is built so a Garmin or Fitbit
// entry could be added here without restructuring the panel.
const CONNECTION_SOURCES = [
  { key: 'strava', label: 'Strava' },
];

export default function UserSettings() {
  const connectionsRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const [distanceUnit, setDistanceUnit] = useState('KILOMETRES');
  const [elevationUnit, setElevationUnit] = useState('METRES');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [stravaConnected, setStravaConnected] = useState(null);
  const [stravaNeedsReauth, setStravaNeedsReauth] = useState(false);
  const [activeSourceTab, setActiveSourceTab] = useState(CONNECTION_SOURCES[0].key);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState(null);

  useEffect(() => {
    getUserSettings()
      .then(data => {
        setDistanceUnit(data.distance_unit || 'KILOMETRES');
        setElevationUnit(data.elevation_unit || 'METRES');
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(getErrorMessage(e, 'Failed to load settings'));
      });
    getStravaConnection()
      .then(data => {
        setStravaConnected(Boolean(data.connected));
        setStravaNeedsReauth(Boolean(data.needsReauth));
      })
      .catch(() => {
        // Connection status is supplementary here - the Connect button
        // below still works even if we couldn't tell whether it's already
        // connected, so this fails quietly rather than blocking settings.
        setStravaConnected(null);
      });
  }, []);

  // Landing on Settings with nothing connected means there's a specific job
  // to do here - covers both the onboarding redirect (a brand-new user, see
  // App.js) and just visiting Settings directly with no connection yet. The
  // Connections panel is below the unit settings, so without this they'd
  // land on top of the page and could easily miss it. Only fires once per
  // visit - reconnecting/disconnecting afterwards shouldn't re-trigger it.
  useEffect(() => {
    if (
      !loading &&
      stravaConnected === false &&
      !hasAutoScrolledRef.current &&
      connectionsRef.current
    ) {
      hasAutoScrolledRef.current = true;
      connectionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, stravaConnected]);

  // This is the only place in the app that starts a Strava connection - there
  // is no separate standalone "connect" screen, so this hands off to
  // Strava's own OAuth page directly rather than routing to an in-app one
  // first.
  const handleConnectClick = () => {
    const stravaUrl = "https://www.strava.com/oauth/authorize";
    const redirectUri = `${window.location.origin}/register/strava`;
    const query = new URLSearchParams({
      client_id: "44713",
      redirect_uri: redirectUri,
      response_type: "code",
      approval_prompt: "auto",
      scope: "activity:read_all",
    });
    window.location.href = `${stravaUrl}?${query.toString()}`;
  };

  const handleDisconnectConfirm = async () => {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await disconnectStrava();
      setStravaConnected(false);
      setStravaNeedsReauth(false);
      setShowDisconnectConfirm(false);
    } catch (e) {
      setDisconnectError(getErrorMessage(e, 'Unable to disconnect Strava.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await setUserSettings(distanceUnit, elevationUnit);
      setSaving(false);
      setSaved(true);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to save settings'));
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-message settings-loading">
        Loading user settings...
    </div>;
  }

  return (
    <main className="settings-page">
      <section className="settings-panel" aria-labelledby="settings-title">
        <div className="settings-heading">
          <div>
            <h2 id="settings-title">Settings</h2>
            <p>Choose how distances and elevation appear in your goals.</p>
          </div>
        </div>
        <div className="settings-fields">
          <div className="settings-field">
            <label htmlFor="distance-unit">Distance</label>
            <select
              id="distance-unit"
              value={distanceUnit}
              onChange={e => { setDistanceUnit(e.target.value); setSaved(false); }}
            >
              {DISTANCE_UNITS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          <div className="settings-field">
            <label htmlFor="elevation-unit">Elevation</label>
            <select
              id="elevation-unit"
              value={elevationUnit}
              onChange={e => { setElevationUnit(e.target.value); setSaved(false); }}
            >
              {ELEVATION_UNITS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <div className="settings-message settings-error" role="alert">{error}</div>}
        {saved && <div className="settings-message settings-saved" role="status">Settings saved.</div>}
        <div className="settings-actions">
          <button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </div>
      </section>
      <section className="settings-panel" aria-labelledby="connections-title" ref={connectionsRef}>
        <div className="settings-heading">
          <div>
            <h2 id="connections-title">Connections</h2>
            <p>Bring activities in automatically from a connected service.</p>
          </div>
        </div>
        <div className="connection-tabs" role="tablist" aria-label="Connected services">
          {CONNECTION_SOURCES.map((source) => (
            <button
              key={source.key}
              type="button"
              role="tab"
              aria-selected={activeSourceTab === source.key}
              className={`connection-tab${activeSourceTab === source.key ? ' active' : ''}`}
              onClick={() => setActiveSourceTab(source.key)}
            >
              {source.label}
            </button>
          ))}
        </div>
        {activeSourceTab === 'strava' && (
          <div className="connection-tab-panel" role="tabpanel">
            {stravaNeedsReauth && (
              <div className="settings-message settings-error" role="alert">
                Strava has revoked access to your account, so activities have stopped syncing. Reconnect below to resume.
              </div>
            )}
            {stravaConnected ? (
              <div className="connections-strava-connected">
                {!stravaNeedsReauth && (
                  <div className="settings-message settings-saved" role="status">
                    Strava is connected. Activities sync in automatically.
                  </div>
                )}
                {stravaNeedsReauth && (
                  <div className="settings-actions">
                    <button onClick={handleConnectClick}>
                      <span aria-hidden="true">↗</span> Reconnect Strava
                    </button>
                  </div>
                )}
                {disconnectError && <div className="settings-message settings-error" role="alert">{disconnectError}</div>}
                <div className="settings-actions">
                  <button
                    className="disconnect-button"
                    onClick={() => { setDisconnectError(null); setShowDisconnectConfirm(true); }}
                  >
                    Disconnect Strava
                  </button>
                </div>
              </div>
            ) : (
              <div className="connections-strava">
                <div className="connections-strava-permissions">
                  <div>
                    <span aria-hidden="true">✓</span>
                    <p><strong>Read your activities</strong><br />We use activity distance, time, and elevation to calculate progress.</p>
                  </div>
                  <div>
                    <span aria-hidden="true">✓</span>
                    <p><strong>Keep your account private</strong><br />We do not publish, edit, or delete anything on Strava.</p>
                  </div>
                </div>
                <button className="connections-strava-connect" onClick={handleConnectClick}>
                  <img src={require("../images/btn_strava_connectwith_orange.png")} alt="Connect with Strava" />
                </button>
                <p className="connections-strava-note">You can disconnect access from Strava at any time.</p>
              </div>
            )}
          </div>
        )}
      </section>
      {showDisconnectConfirm && (
        <div className="modal-overlay" onClick={() => !disconnecting && setShowDisconnectConfirm(false)}>
          <div
            className="modal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="disconnect-strava-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="disconnect-strava-title">Disconnect Strava?</h3>
            <p>
              This removes your Strava connection and deletes every activity imported from Strava.
              Activities from any other connected source won't be affected. This can't be undone.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDisconnectConfirm(false)} disabled={disconnecting}>
                Cancel
              </button>
              <button type="button" className="modal-danger-button" onClick={handleDisconnectConfirm} disabled={disconnecting}>
                {disconnecting ? 'Disconnecting...' : 'Disconnect and delete data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
